function asTrimmedString(value) {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return "";
}

function flattenErrorValues(value, depth = 0) {
  if (depth > 4 || value == null) return [];
  if (typeof value === "string" || typeof value === "number") {
    const text = asTrimmedString(value);
    return text ? [text] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenErrorValues(item, depth + 1));
  }
  if (typeof value === "object") {
    return Object.values(value).flatMap((item) =>
      flattenErrorValues(item, depth + 1)
    );
  }
  return [];
}

function pickPayload(errorOrResponse) {
  if (errorOrResponse == null) return {};

  // Axios error
  if (errorOrResponse.response) {
    return errorOrResponse.response.data ?? {};
  }

  // Axios success response
  if (errorOrResponse.data !== undefined && errorOrResponse.status != null) {
    return errorOrResponse.data ?? {};
  }

  // Already unwrapped body / plain string
  return errorOrResponse;
}

/** Pull a human-readable message from an evaluation_images API result or axios error. */
export function getUploadErrorMessage(errorOrResponse) {
  const data = pickPayload(errorOrResponse);

  if (typeof data === "string") {
    return data.trim() || "Photo upload failed. Please try again.";
  }

  const nested =
    data && typeof data === "object"
      ? data.data || data.result || data.payload || null
      : null;

  const candidates = [
    data?.message,
    data?.Message,
    data?.msg,
    data?.Msg,
    data?.detail,
    data?.Detail,
    data?.error,
    data?.Error,
    data?.reason,
    data?.description,
    nested?.message,
    nested?.Message,
    nested?.msg,
    nested?.detail,
    nested?.error,
    typeof data?.errors === "string" ? data.errors : null,
    ...flattenErrorValues(data?.errors),
    ...flattenErrorValues(data?.non_field_errors),
    errorOrResponse?.message,
    errorOrResponse?.statusText,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "object" && candidate !== null) {
      const nestedText = flattenErrorValues(candidate).join(" ");
      if (nestedText) return nestedText;
      continue;
    }
    const text = asTrimmedString(candidate);
    if (text && text.toLowerCase() !== "error") {
      return text;
    }
  }

  const status = errorOrResponse?.response?.status || errorOrResponse?.status;
  if (status === 402 || status === 403) {
    return "Insufficient credits. Please top up your credits and try again.";
  }

  return "Photo upload failed. Please try again.";
}

export function isInsufficientCreditMessage(message) {
  const text = String(message || "").toLowerCase();
  return (
    text.includes("insufficient") ||
    text.includes("insufficent") ||
    text.includes("not enough credit") ||
    text.includes("no credit") ||
    text.includes("credits are zero") ||
    text.includes("0 credit") ||
    (text.includes("credit") &&
      (text.includes("lack") ||
        text.includes("enough") ||
        text.includes("balance") ||
        text.includes("top up") ||
        text.includes("top-up")))
  );
}

export function isEvaluationImagesError(response) {
  const data = response?.data;
  if (!data) return true;
  if (typeof data === "string") {
    const lower = data.toLowerCase();
    return (
      lower.includes("error") ||
      lower.includes("fail") ||
      lower.includes("insufficient") ||
      lower.includes("credit")
    );
  }
  if (data.status === false || data.success === false) return true;
  if (String(data.status || "").toLowerCase() === "error") return true;
  if (data.error) return true;
  if (typeof data.errors === "string" && data.errors.trim()) return true;
  if (
    data.errors &&
    typeof data.errors === "object" &&
    Object.keys(data.errors).length > 0
  ) {
    return true;
  }
  if (data.msg && /error|fail|insufficient|credit/i.test(String(data.msg))) {
    return true;
  }
  return false;
}
